import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useList } from "@/lib/hooks";
import api from "@/lib/api";
import { Panel, money } from "@/components/common";
import {
  Sun, Cloud, CloudRain, CloudSnow, CloudFog, Lightning, CloudSun, ArrowRight, ChartLineUp,
} from "@phosphor-icons/react";
import { ResponsiveContainer, AreaChart, Area, XAxis, Tooltip } from "recharts";

const ZONES = [
  { label: "Pacific", tz: "America/Los_Angeles", abbr: "PT" },
  { label: "Mountain", tz: "America/Denver", abbr: "MT" },
  { label: "Central", tz: "America/Chicago", abbr: "CT" },
  { label: "Eastern", tz: "America/New_York", abbr: "ET" },
];

function fmt(tz, now) {
  return new Intl.DateTimeFormat("en-US", { timeZone: tz, hour: "2-digit", minute: "2-digit", hour12: true }).format(now);
}

function fmtDay(tz, now) {
  return new Intl.DateTimeFormat("en-US", { timeZone: tz, weekday: "short", month: "short", day: "numeric" }).format(now);
}

export function TimeZones() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <Panel className="p-5" data-testid="dashboard-timezones">
      <h3 className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-4">US Time Zones</h3>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {ZONES.map((z) => (
          <div key={z.tz} className="rounded-xl bg-gradient-to-br from-slate-50 to-white dark:from-zinc-900 dark:to-black border border-slate-200 dark:border-zinc-800 px-5 py-5 text-center hover:border-blue-200 transition-colors" data-testid={`tz-${z.abbr}`}>
            <p className="text-sm font-semibold text-slate-500">{z.label} <span className="text-slate-300">· {z.abbr}</span></p>
            <p className="font-heading font-extrabold text-3xl lg:text-4xl tz-time tabular tracking-tight mt-2">{fmt(z.tz, now)}</p>
            <p className="text-xs text-slate-400 mt-1.5">{fmtDay(z.tz, now)}</p>
          </div>
        ))}
      </div>
    </Panel>
  );
}

const HUBS = ["Chicago", "Dallas", "Los Angeles", "Atlanta"];

function wIcon(code) {
  if (code === 0) return Sun;
  if ([1, 2].includes(code)) return CloudSun;
  if (code === 3) return Cloud;
  if ([45, 48].includes(code)) return CloudFog;
  if ([71, 73, 75, 77, 85, 86].includes(code)) return CloudSnow;
  if ([95, 96, 99].includes(code)) return Lightning;
  if (code >= 51) return CloudRain;
  return CloudSun;
}
function wLabel(code) {
  if (code === 0) return "Clear";
  if ([1, 2].includes(code)) return "Partly cloudy";
  if (code === 3) return "Overcast";
  if ([45, 48].includes(code)) return "Fog";
  if ([71, 73, 75, 77, 85, 86].includes(code)) return "Snow";
  if ([95, 96, 99].includes(code)) return "Storm";
  if (code >= 51) return "Rain";
  return "—";
}

export function WeatherHubs() {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let active = true;
    (async () => {
      const out = {};
      await Promise.all(HUBS.map(async (city) => {
        try {
          const res = await api.get(`/weather?city=${encodeURIComponent(city)}`);
          out[city] = res.data;
        } catch (e) { out[city] = null; }
      }));
      if (active) { setData(out); setLoading(false); }
    })();
    return () => { active = false; };
  }, []);
  return (
    <Panel className="p-4" data-testid="dashboard-weather">
      <h3 className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-3">Freight Hub Weather</h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {HUBS.map((city) => {
          const d = data[city];
          const code = d?.current?.weather_code;
          const Icon = wIcon(code);
          return (
            <div key={city} className="rounded-lg bg-gradient-to-br from-blue-50 to-slate-50 border border-blue-100 px-3 py-2.5 flex items-center gap-3" data-testid={`weather-${city.replace(/\s+/g, "-")}`}>
              <Icon size={30} weight="duotone" className="text-blue-600 shrink-0" />
              <div className="min-w-0">
                <p className="text-[11px] font-semibold text-slate-500 truncate">{city}</p>
                {loading ? <p className="text-sm text-slate-400">…</p> : d ? (
                  <>
                    <p className="font-heading font-bold text-lg text-slate-900 leading-none">{Math.round(d.current?.temperature_2m)}°F</p>
                    <p className="text-[11px] text-slate-500">{wLabel(code)}</p>
                  </>
                ) : <p className="text-xs text-slate-400">N/A</p>}
              </div>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}

export function AnalyticsMini() {
  const { data } = useList("reports", "/reports");
  const trend = data?.revenue_trend || [];
  const pipeline = data?.pipeline || [];
  const leads = pipeline.reduce((s, p) => s + (p.count || 0), 0);
  const totalRev = trend.reduce((s, t) => s + (t.revenue || 0), 0);
  return (
    <Panel className="p-5" data-testid="dashboard-analytics">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2"><ChartLineUp size={18} className="text-blue-600" />Analytics Report</h3>
        <Link to="/reports" className="text-sm font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1" data-testid="view-full-report">View full report <ArrowRight size={15} /></Link>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">
          <p className="text-xs text-slate-400">Revenue (trend)</p>
          <p className="font-heading font-bold text-xl text-slate-900">{money(totalRev)}</p>
        </div>
        <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">
          <p className="text-xs text-slate-400">Leads in pipeline</p>
          <p className="font-heading font-bold text-xl text-slate-900">{leads}</p>
        </div>
      </div>
      {trend.length === 0 ? (
        <p className="text-sm text-slate-400 py-6 text-center">No revenue data yet.</p>
      ) : (
        <ResponsiveContainer width="100%" height={140}>
          <AreaChart data={trend} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
            <defs>
              <linearGradient id="miniRev" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#2563eb" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#2563eb" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="month" stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
            <Tooltip formatter={(v) => money(v)} contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} />
            <Area type="monotone" dataKey="revenue" stroke="#2563eb" strokeWidth={2.5} fill="url(#miniRev)" />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </Panel>
  );
}
